// story: видео-слоты существ — <video> + WebGL-текстура на каждый файл.
//
// Петли (ходьба, призраки, картины) — один общий плеер на файл: все зомби
// шагают в такт, зато декодер один. Однократные клипы (удар, смерть) — свой
// плеер у существа из небольшого пула: смерть одного зомби не должна
// перематывать смерть другого. Лежащее тело (последний кадр смерти) — снова
// один общий плеер на файл, запаркованный в конце ролика: трупы не шевелятся,
// а плеер из пула освобождается, как только клип смерти доигран.
//
// Файла может не быть (клипы ещё не сгенерированы) — тогда цепочка:
// свой .webm/.mov → fallback-слот → заглушка zombi.webm → рисованный силуэт.
// Ошибка загрузки любого звена не роняет игру, слот просто едет дальше.
// Кадр видео грузится в текстуру не чаще ~30 раз в секунду и только если
// существо с этим слотом рисуется в кадре.

import { STORY_VIDEO_DIR, STUB_VIDEO, VIDEO_SLOTS } from "./catalog";
import { videoUrlForBrowser } from "../engine/meshLoader";

/** WebKit (Safari и все браузеры iOS) — .pack.mp4 с альфой в нижней половине
 *  кадра (как meshLoader). */
function forBrowser(url: string): string {
  return videoUrlForBrowser(url);
}

const UPLOAD_MIN_MS = 30;
/** Не нужен столько — пауза (экономим декодер). */
const IDLE_PAUSE_MS = 1500;
const ONESHOT_POOL_PER_URL = 3;
/** Насколько раньше конца ролика паркуется «последний кадр» (с). */
const END_PARK_SEC = 0.04;

/** Один файл: элемент <video> и его текстура. */
export class VideoSource {
  readonly url: string;
  readonly video: HTMLVideoElement;
  tex: WebGLTexture | null = null;
  ready = false;
  failed = false;
  private uploadedTime = -1;
  private uploadedAt = 0;
  private uploadedOnce = false;
  lastUsed = 0;
  wantPlay = false;
  /** Плеер «последнего кадра»: кадр берём, только когда ролик доигран до
   *  конца (пока идёт перемотка, видео ещё показывает первый кадр — стоящее
   *  тело, а currentTime уже конечный, и повторной заливки бы не было). */
  private readonly holdEnd: boolean;
  private parked = false;

  constructor(url: string, loop: boolean, holdEnd = false, rate = 1) {
    this.url = url;
    this.holdEnd = holdEnd;
    const v = document.createElement("video");
    v.crossOrigin = "anonymous";
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.loop = loop;
    if (rate !== 1) {
      // До src: загрузка сбрасывает playbackRate в defaultPlaybackRate.
      v.defaultPlaybackRate = rate;
      v.playbackRate = rate;
    }
    v.addEventListener("loadeddata", () => { this.ready = true; }, { once: true });
    v.addEventListener("error", () => { this.failed = true; }, { once: true });
    if (holdEnd) {
      // Перемотка к концу и play(): ролик доигрывает последние 40 мс и
      // замирает на последнем кадре. play() нужен iOS — там не сыгранное
      // видео может так и не декодировать ни одного кадра.
      const park = (): void => {
        const d = v.duration;
        if (Number.isFinite(d) && d > END_PARK_SEC) {
          try { v.currentTime = d - END_PARK_SEC; } catch { /* */ }
        }
        void v.play().catch(() => { /* autoplay — кадр всё равно будет после seeked */ });
      };
      v.addEventListener("loadedmetadata", park, { once: true });
      v.addEventListener("ended", () => { this.parked = true; }, { once: true });
      v.addEventListener("seeked", () => {
        if (Number.isFinite(v.duration) && v.currentTime >= v.duration - END_PARK_SEC * 2) this.parked = true;
      });
    }
    v.src = forBrowser(url);
    this.video = v;
  }

  /** Однократный ролик доигран (стоит на последнем кадре). */
  done(): boolean {
    const v = this.video;
    if (v.ended) return true;
    const d = v.duration;
    return Number.isFinite(d) && d > 0 && v.readyState >= 2 && v.currentTime >= d - 0.08;
  }

  /** Залить текущий кадр в текстуру (не чаще ~30 к/с и только при смене кадра). */
  upload(gl: WebGL2RenderingContext, nowMs: number): WebGLTexture | null {
    const v = this.video;
    if (this.holdEnd) {
      // Последний кадр не меняется: залили один раз — и хватит.
      if (!this.parked) return null;
      if (this.uploadedOnce) return this.tex;
    }
    if (this.failed || v.readyState < 2) return this.uploadedOnce ? this.tex : null;
    if (!this.tex) {
      this.tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    }
    const t = v.currentTime;
    if (this.uploadedOnce && (t === this.uploadedTime || nowMs - this.uploadedAt < UPLOAD_MIN_MS)) {
      return this.tex;
    }
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, v);
      this.uploadedOnce = true;
      this.uploadedTime = t;
      this.uploadedAt = nowMs;
    } catch {
      // Кросс-доменный/битый кадр — оставляем прошлый.
    }
    return this.uploadedOnce ? this.tex : null;
  }

  dispose(gl: WebGL2RenderingContext): void {
    try { this.video.pause(); } catch { /* */ }
    this.video.removeAttribute("src");
    try { this.video.load(); } catch { /* */ }
    if (this.tex) gl.deleteTexture(this.tex);
    this.tex = null;
  }
}

/** Однократный клип у конкретного существа. */
export interface OneShot {
  src: VideoSource;
  slot: string;
  busy: boolean;
}

/** Цепочка кандидатов слота. */
interface SlotChain {
  urls: string[];
  idx: number;
}

export class VideoBank {
  private gl: WebGL2RenderingContext;
  private byUrl = new Map<string, VideoSource>();
  private chains = new Map<string, SlotChain>();
  private pools = new Map<string, OneShot[]>();
  private ends = new Map<string, VideoSource>();
  private procedural = new Map<string, WebGLTexture>();

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
  }

  private chainFor(slot: string): SlotChain {
    let c = this.chains.get(slot);
    if (c) return c;
    const urls: string[] = [];
    const seen = new Set<string>();
    let s: string | undefined = slot;
    let noStub = false;
    for (let guard = 0; s && guard < 4; guard++) {
      const def: (typeof VIDEO_SLOTS)[string] | undefined = VIDEO_SLOTS[s];
      if (!def) break;
      if (def.noStub) noStub = true;
      const u = `${STORY_VIDEO_DIR}${def.name}.webm`;
      if (!seen.has(u)) { urls.push(u); seen.add(u); }
      s = def.fallback;
    }
    if (!noStub) urls.push(STUB_VIDEO);
    c = { urls, idx: 0 };
    this.chains.set(slot, c);
    return c;
  }

  private source(url: string, loop: boolean): VideoSource {
    let src = this.byUrl.get(url);
    if (!src) {
      src = new VideoSource(url, loop);
      this.byUrl.set(url, src);
    }
    return src;
  }

  /** Общий (петлевой) источник слота; null — пока грузится или всё упало. */
  shared(slot: string, nowMs: number): VideoSource | null {
    const c = this.chainFor(slot);
    while (c.idx < c.urls.length) {
      const src = this.source(c.urls[c.idx]!, true);
      if (src.failed) {
        c.idx++;
        continue;
      }
      src.lastUsed = nowMs;
      return src;
    }
    return null;
  }

  /** Тот же петлевой ролик слота, но со своей скоростью проигрывания: шаги
   *  ходьбы под скорость существа (зомби в погоне и бродящий шагают в разном
   *  темпе). Общий плеер на каждую скорость; пока он не готов — обычный. */
  sharedAt(slot: string, nowMs: number, rate: number): VideoSource | null {
    const base = this.shared(slot, nowMs);
    if (!base || rate === 1) return base;
    const key = `${base.url}#${rate}`;
    let src = this.byUrl.get(key);
    if (!src) {
      src = new VideoSource(base.url, true, false, rate);
      this.byUrl.set(key, src);
    }
    src.lastUsed = nowMs;
    return src.ready && !src.failed ? src : base;
  }

  /** Слот гарантированно упал целиком (рисуем силуэт). */
  exhausted(slot: string): boolean {
    const c = this.chainFor(slot);
    return c.idx >= c.urls.length;
  }

  /** Свой однократный плеер слота (null — пул занят или файл ещё не известен). */
  acquireOneShot(slot: string, nowMs: number): OneShot | null {
    const shared = this.shared(slot, nowMs);
    if (!shared || !shared.ready) return null;
    const url = shared.url;
    let pool = this.pools.get(url);
    if (!pool) {
      pool = [];
      this.pools.set(url, pool);
    }
    let os = pool.find((o) => !o.busy && !o.src.failed);
    if (!os && pool.length < ONESHOT_POOL_PER_URL) {
      os = { src: new VideoSource(url, false), slot, busy: false };
      pool.push(os);
    }
    if (!os) return null;
    os.busy = true;
    os.slot = slot;
    os.src.lastUsed = nowMs;
    return os;
  }

  /** Последний кадр однократного клипа (тело лежит) — общий на всех; null —
   *  пока не готов. Вызывать заранее (activate), чтобы к первой смерти кадр
   *  уже стоял. */
  endFrame(slot: string, nowMs: number): VideoSource | null {
    const shared = this.shared(slot, nowMs);
    if (!shared) return null;
    let src = this.ends.get(shared.url);
    if (!src) {
      src = new VideoSource(shared.url, false, true);
      this.ends.set(shared.url, src);
    }
    if (src.failed) return null;
    src.lastUsed = nowMs;
    return src;
  }

  releaseOneShot(os: OneShot | null): void {
    if (!os) return;
    os.busy = false;
    try { os.src.video.pause(); } catch { /* */ }
  }

  /** Рисованная заглушка (силуэт/книга), если видео нет вовсе. */
  proceduralTex(key: string, draw: (c: CanvasRenderingContext2D, w: number, h: number) => void): WebGLTexture | null {
    const ready = this.procedural.get(key);
    if (ready) return ready;
    const gl = this.gl;
    const cv = document.createElement("canvas");
    cv.width = 256;
    cv.height = 256;
    const c2 = cv.getContext("2d");
    if (!c2) return null;
    draw(c2, 256, 256);
    const tex = gl.createTexture();
    if (!tex) return null;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, cv);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.procedural.set(key, tex);
    return tex;
  }

  /** Раз в кадр: играть нужные петли, ставить на паузу ненужные. */
  tick(nowMs: number): void {
    for (const src of this.byUrl.values()) {
      if (src.failed) continue;
      const v = src.video;
      const want = src.wantPlay && nowMs - src.lastUsed < IDLE_PAUSE_MS;
      if (want && v.paused) void v.play().catch(() => { /* autoplay */ });
      else if (!want && !v.paused) v.pause();
      src.wantPlay = false;
    }
  }

  /** Сбросить всё (выход из режима). */
  dispose(): void {
    const gl = this.gl;
    for (const src of this.byUrl.values()) src.dispose(gl);
    for (const pool of this.pools.values()) for (const os of pool) os.src.dispose(gl);
    for (const src of this.ends.values()) src.dispose(gl);
    for (const t of this.procedural.values()) gl.deleteTexture(t);
    this.byUrl.clear();
    this.pools.clear();
    this.ends.clear();
    this.chains.clear();
    this.procedural.clear();
  }
}
